import { type DatabaseChangeType } from '@databases/types/redux';
import {
  addChangeToChangeset,
  type DatabaseChange,
  type DatabaseChangeset,
} from '@databases/utils/database/changesets';
import { createSlice, type PayloadAction } from '@databases/utils/shims/redux-toolkit';

export interface ChangesetsState {
  draftedChangesets: Record<string, DatabaseChangeset>;
  savingChangeset: Record<string, DatabaseChangeset>;
  activeSaveOperations: Record<string, number>;
}

export const initialChangesetsState: ChangesetsState = {
  draftedChangesets: {},
  savingChangeset: {},
  activeSaveOperations: {},
};

const changesetsSlice = createSlice({
  name: 'databaseUpdates',
  initialState: initialChangesetsState,
  reducers: {
    addChangesToDraft: (
      state,
      action: PayloadAction<{
        contentId: string;
        changes: DatabaseChange[];
        before?: boolean;
      }>
    ) => {
      const { contentId, changes, before } = action.payload;

      if (before) {
        state.draftedChangesets[contentId] = addChangeToChangeset(
          [],
          ...changes,
          ...state.draftedChangesets[contentId]
        );
      } else {
        state.draftedChangesets[contentId] = addChangeToChangeset(
          state.draftedChangesets[contentId] || [],
          ...changes
        );
      }
    },

    removeChangesFromDraft: (
      state,
      action: PayloadAction<{
        contentId: string;
        changes: DatabaseChangeType[];
      }>
    ) => {
      const { contentId, changes } = action.payload;

      state.draftedChangesets[contentId] = state.draftedChangesets[contentId].filter(
        (change) => !changes.includes(change.type)
      );
    },

    setDraftedChangeset: (
      state,
      action: PayloadAction<{
        contentId: string;
        changeset: DatabaseChangeset;
      }>
    ) => {
      const { contentId, changeset } = action.payload;

      state.draftedChangesets[contentId] = changeset;
    },

    setSavingChangeset: (
      state,
      action: PayloadAction<{
        contentId: string;
        changeset: DatabaseChangeset;
      }>
    ) => {
      const { contentId, changeset } = action.payload;

      state.savingChangeset[contentId] = changeset;
    },

    startSaveOperation: (state, action: PayloadAction<{ contentId: string }>) => {
      const { contentId } = action.payload;

      state.activeSaveOperations[contentId] =
        (state.activeSaveOperations[contentId] ?? 0) + 1;
    },

    endSaveOperation: (state, action: PayloadAction<{ contentId: string }>) => {
      const { contentId } = action.payload;

      state.activeSaveOperations[contentId] =
        (state.activeSaveOperations[contentId] ?? 0) - 1;
    },
  },
});

export default changesetsSlice;
